import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Boton from '../../components/Boton'
import { npApi } from '../../api/compras'
import { fmtFecha, fmtFechaHora } from '../../utils/fecha'
import { colors, text, space, font } from '../../design/tokens'
import { nuevoUuid, mensajeError, st } from '../emp900/_helpers'
import { Badge, CajaError } from '../emp900/_comun'
import { NP_ESTADO_LABEL, NP_ESTADO_TONO, MOTIVO_CIERRE, CONDICION_TIPO, avanceLinea, fmtMoneda, fmtPct, fmtCant } from './_np'
import { EnviarModal, CancelarSaldoModal, AnularModal } from './_npModales'

/**
 * Consulta de un pedido: lo acordado, el seguimiento renglón por renglón
 * (pedido / recibido / cancelado / saldo) y las acciones del ciclo.
 */
export default function NotaPedidoDetallePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [p, setP] = useState(null)
  const [error, setError] = useState('')
  const [modal, setModal] = useState(null) // 'enviar' | 'cancelar' | 'anular'
  const [trabajando, setTrabajando] = useState(false)

  const cargar = useCallback(async () => {
    try { const { data } = await npApi.obtener(id); setP(data.pedido); setError('') } catch (e) { setError(mensajeError(e)) }
  }, [id])
  useEffect(() => { cargar() }, [cargar])

  const accion = async (fn, despues) => {
    setTrabajando(true); setError('')
    try { const r = await fn(); await cargar(); setModal(null); despues?.(r) } catch (e) { setError(mensajeError(e)) } finally { setTrabajando(false) }
  }

  if (!p) return error ? <CajaError>{error}</CajaError> : <p style={st.info}>Cargando…</p>

  const items = p.items || []
  const mon = p.moneda || 'ARS'
  const tieneRecepciones = (p.recepciones || []).length > 0
  const conSaldo = items.some(it => Number(it.saldo) > 0)
  const esBorrador = p.estado === 'borrador'
  const esEmitido = p.estado === 'emitido'
  const suma = (k) => items.reduce((t, i) => t + Number(i[k] || 0), 0)
  const totNeto = suma('neto'), totIva = suma('iva'), totInternos = suma('internos'), totOtros = suma('otros_cargos'), totFinal = suma('costo_final')
  const dato = (label, valor) => (
    <div><div style={{ fontSize: text.meta, color: colors.textMeta, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div><div style={{ fontSize: text.body, color: colors.text }}>{valor || '—'}</div></div>
  )
  const COLS = ['Código', 'Detalle', 'P. lista', 'Dto1', 'Dto2', 'Dto3', 'Precio', 'Pedido', 'Recibido', 'Cancelado', 'Saldo', 'Avance', 'IVA', 'Internos', 'Total']
  const DER = new Set(['P. lista', 'Dto1', 'Dto2', 'Dto3', 'Precio', 'Pedido', 'Recibido', 'Cancelado', 'Saldo', 'IVA', 'Internos', 'Total'])

  return (
    <div>
      <div style={st.header}>
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: space.md, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: font.mono, fontSize: text.display, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{p.numero}</span>
            <Badge tono={NP_ESTADO_TONO[p.estado] || 'neutro'}>{NP_ESTADO_LABEL[p.estado] || p.estado}</Badge>
            {p.reemplaza_a_id && <span style={{ color: colors.textSec, fontSize: text.sec }}>reemplaza a la NP #{p.reemplaza_a_id}</span>}
          </div>
          <div style={{ color: colors.textSec, fontSize: text.sec, marginTop: space.xs }}>
            {p.proveedor_nombre} · {fmtFecha(p.fecha)}{p.emitido_en ? ` · emitida ${fmtFechaHora(p.emitido_en)}` : ''}
          </div>
        </div>
        <div style={st.acciones}>
          <Boton variante="ghost" onClick={() => navigate('/compras/pedidos/consultas')}>← Consultas</Boton>
          {esBorrador && <Boton variante="secondary" onClick={() => navigate(`/compras/pedidos/${p.id}/editar`)}>✎ Modificar</Boton>}
          {esBorrador && <Boton variante="primary" disabled={trabajando || items.length === 0} onClick={() => accion(() => npApi.emitir(p.id, nuevoUuid()))}>✅ Emitir</Boton>}
          {!esBorrador && p.estado !== 'anulado' && <Boton variante="send" onClick={() => setModal('enviar')}>✉ Enviar al proveedor</Boton>}
          <Boton variante="secondary" onClick={() => npApi.descargarPdf(p.id, p.numero)}>⬇ Reporte</Boton>
          {esEmitido && !tieneRecepciones && <Boton variante="secondary" disabled={trabajando} onClick={() => accion(() => npApi.nuevaVersion(p.id, nuevoUuid()), r => r?.data?.pedido?.id && navigate(`/compras/pedidos/${r.data.pedido.id}/editar`))}>⎘ Nueva versión</Boton>}
          {esEmitido && conSaldo && <Boton variante="secondary" onClick={() => setModal('cancelar')}>Cancelar saldo…</Boton>}
          {(esBorrador || (esEmitido && !tieneRecepciones)) && <Boton variante="destructive" onClick={() => setModal('anular')}>Anular</Boton>}
        </div>
      </div>
      {error && <CajaError>{error}</CajaError>}
      {esBorrador && <div style={st.aviso}>📝 Borrador: todavía no compromete nada. Emitilo para congelar lo acordado y poder enviarlo.</div>}
      {p.estado === 'anulado' && p.motivo_anulacion && <div style={st.aviso}>Anulado: {p.motivo_anulacion}</div>}

      <div style={st.card}>
        <div style={st.cardTitulo}>Pedido</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: space.lg, rowGap: space.md }}>
          {dato('Condición', p.condicion_pago)}
          {dato('Moneda', mon === 'USD' ? `Dólares (USD)${p.cotizacion ? ` · cotización $${Number(p.cotizacion).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : ''}` : 'Pesos (ARS)')}
          {dato('Bonificación general', Number(p.bonificacion_pct) ? fmtPct(p.bonificacion_pct) : '—')}
          {dato('Nº pedido del proveedor', p.numero_pedido_proveedor)}
          {dato('Lugar de entrega', p.lugar_entrega)}
          {dato('Fecha de entrega', p.fecha_entrega ? fmtFecha(p.fecha_entrega) : '')}
          {dato('Depósito', p.deposito_nombre || p.deposito)}
          {dato('Observaciones', p.observaciones)}
        </div>
      </div>

      <div style={{ ...st.card, marginTop: space.lg }}>
        <div style={st.cardTitulo}>Productos y seguimiento</div>
        <div style={st.tableWrap}>
          <table style={{ ...st.table, minWidth: 1400 }}>
            <thead>
              <tr>{COLS.map(h => <th key={h} style={{ ...st.th, ...(DER.has(h) ? { textAlign: 'right' } : {}) }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {items.map(it => {
                const av = avanceLinea(it)
                return (
                  <tr key={it.id} style={st.tr}>
                    <td style={st.tdMono}>{it.sku}{it.codigo_proveedor ? <div style={{ fontWeight: 400, fontSize: text.meta, color: colors.textMeta }}>prov. {it.codigo_proveedor}</div> : null}</td>
                    <td style={{ ...st.td, minWidth: 260 }}><div>{it.descripcion}</div>{it.ean ? <div style={{ fontFamily: font.mono, fontSize: text.meta, color: colors.textMeta }}>EAN {it.ean}</div> : null}{it.motivo_cierre && <div style={{ fontSize: text.sec, color: colors.warn }}>Cancelado: {MOTIVO_CIERRE[it.motivo_cierre] || it.motivo_cierre}{it.comentario_cierre ? ` — ${it.comentario_cierre}` : ''}</div>}</td>
                    <td style={st.tdImporte}>{fmtMoneda(it.precio_lista, mon)}</td>
                    <td style={st.tdImporte}>{fmtPct(it.descuento1_pct ?? it.descuento_pct)}</td>
                    <td style={st.tdImporte}>{fmtPct(it.descuento2_pct)}</td>
                    <td style={st.tdImporte}>{fmtPct(it.descuento3_pct)}</td>
                    <td style={st.tdImporte}>{fmtMoneda(it.precio ?? it.costo_unitario_neto, mon, 4)}</td>
                    <td style={st.tdImporte}>{fmtCant(it.cantidad)}</td>
                    <td style={st.tdImporte}>{fmtCant(it.cantidad_recibida)}</td>
                    <td style={st.tdImporte}>{Number(it.cantidad_cerrada) ? fmtCant(it.cantidad_cerrada) : '—'}</td>
                    <td style={{ ...st.tdImporte, color: Number(it.saldo) > 0 ? colors.text : colors.textMeta }}>{fmtCant(it.saldo)}</td>
                    <td style={{ ...st.td, minWidth: 90 }}><div style={{ height: 6, background: colors.border, borderRadius: 3, overflow: 'hidden' }}><div style={{ width: `${av.pct}%`, height: '100%', background: av.color, borderRadius: 3 }} /></div><div style={{ fontFamily: font.mono, fontSize: text.meta, color: colors.textMeta, marginTop: 2 }}>{av.pct}%</div></td>
                    <td style={st.tdImporte}>{fmtMoneda(it.iva, mon)}<div style={{ fontSize: text.meta, color: colors.textMeta, fontWeight: 400 }}>{fmtPct(it.iva_pct)}</div></td>
                    <td style={st.tdImporte}>{Number(it.internos) ? fmtMoneda(it.internos, mon) : '—'}{Number(it.internos_pct) ? <div style={{ fontSize: text.meta, color: colors.textMeta, fontWeight: 400 }}>{fmtPct(it.internos_pct)}</div> : null}</td>
                    <td style={st.tdImporte}>{fmtMoneda(it.costo_final, mon)}</td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={COLS.length - 1} style={{ ...st.td, textAlign: 'right', color: colors.textSec, fontSize: text.sec }}>
                  Neto{Number(p.bonificacion_pct) ? ` (bonif. general ${fmtPct(p.bonificacion_pct)})` : ''} {fmtMoneda(totNeto, mon)} · IVA {fmtMoneda(totIva, mon)}{totInternos ? ` · internos ${fmtMoneda(totInternos, mon)}` : ''}{totOtros ? ` · otros ${fmtMoneda(totOtros, mon)}` : ''} · <b>TOTAL</b>
                </td>
                <td style={{ ...st.tdImporte, fontSize: text.title }}>{fmtMoneda(totFinal, mon)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.lg, marginTop: space.lg }}>
        <div style={st.card}>
          <div style={st.cardTitulo}>Condiciones comerciales especiales (congeladas al emitir)</div>
          {(p.condiciones || []).length === 0 ? <p style={st.info}>Sin condiciones especiales.</p> : (
            <ul style={{ margin: 0, paddingLeft: space.xl, fontSize: text.body, color: colors.text }}>
              {p.condiciones.map((c, i) => <li key={i} style={{ margin: `${space.xs}px 0` }}><b>{CONDICION_TIPO[c.tipo] || c.tipo}</b>: {c.descripcion}{c.porcentaje ? ` · ${fmtPct(c.porcentaje)}` : ''}{c.importe ? ` · ${fmtMoneda(c.importe, mon)}` : ''}{c.lleva && c.paga ? ` · lleva ${c.lleva} paga ${c.paga}` : ''}{c.vigencia_hasta ? ` · hasta ${fmtFecha(c.vigencia_hasta)}` : ''}<span style={{ color: colors.textMeta, fontSize: text.sec }}> ({c.origen === 'acuerdo' ? 'del acuerdo' : 'de esta operación'})</span></li>)}
            </ul>
          )}
        </div>
        <div style={st.card}>
          <div style={st.cardTitulo}>Envíos al proveedor</div>
          {(p.envios || []).length === 0 ? <p style={st.info}>Todavía no se envió.</p> : (
            <table style={st.table}><tbody>
              {p.envios.map((e, i) => (
                <tr key={i} style={st.tr}>
                  <td style={st.tdFecha}>{fmtFechaHora(e.enviado_en)}</td>
                  <td style={st.td}>{{ email: '✉ Mail', whatsapp: '📱 WhatsApp', manual: '🤝 Manual / impreso' }[e.canal] || e.canal}</td>
                  <td style={st.tdSec}>{e.destinatario || '—'}</td>
                  <td style={st.tdSec}>{e.usuario || e.usuario_nombre || ''}</td>
                </tr>
              ))}
            </tbody></table>
          )}
          <div style={{ ...st.cardTitulo, marginTop: space.lg }}>Recepciones</div>
          {(p.recepciones || []).length === 0 ? <p style={st.info}>Sin recepciones.</p> : (
            <table style={st.table}><tbody>
              {p.recepciones.map(r => (
                <tr key={r.id} style={st.tr}>
                  <td style={st.tdMono}>{r.numero}</td>
                  <td style={st.tdFecha}>{fmtFecha(r.fecha)}</td>
                  <td style={st.tdSec}>{r.remito_proveedor ? `remito ${r.remito_proveedor}` : ''}</td>
                  <td style={st.td}><Badge tono={r.estado === 'anulada' ? 'err' : 'ok'}>{r.estado}</Badge></td>
                </tr>
              ))}
            </tbody></table>
          )}
        </div>
      </div>

      {modal === 'enviar' && <EnviarModal p={p} trabajando={trabajando} error={error} onClose={() => setModal(null)} accion={accion} />}
      {modal === 'cancelar' && <CancelarSaldoModal p={p} trabajando={trabajando} error={error} onClose={() => setModal(null)} accion={accion} />}
      {modal === 'anular' && <AnularModal p={p} trabajando={trabajando} error={error} onClose={() => setModal(null)} accion={accion} />}
    </div>
  )
}
